import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type {
  OrderProductsDataType,
  OrderV2EventType,
} from 'pages/Order/components/Detail/mapOrderProducts'

export const SLICE_NAME = 'orderDetail'

export type CustomerType = {
  name: string
  phone: string
  address: string
  latitude: number
  longitude: number
  addressDetail: string
  driverNote: string
  labelAddress: string
  addressImageUrls: string[]
}

// Sourced from `GET /oms/internal/v2/orders/{order_id}`'s `customer` field (fetched
// alongside order products) — that endpoint doesn't return a buyer address.
export type BuyerType = {
  name: string
  phone: string
}

export type VoucherType = {
  benefitType: string
  code: string
  amount: number
  startPeriod: number
  endPeriod: number
  minPurchase: number
}

export type PlatformFeeType = {
  amount: number
  paymentFee: number
  packagingFee: number
  nightFee: number
}

export type PaymentType = {
  method: string
  name: string
  amount: number
  partner: string
  deliveryFee: number
  voucher: VoucherType
  voucherList: Nullable<VoucherType[]>
  astroBalance: number
  totalCoinUsed: number
  totalCoinEarned: number
  totalPriceItem: number
  platformFee: PlatformFeeType
  parentId: number
  surgeFee: number
}

export type TimeslotType = {
  minArrivalAt: number
  maxArrivalAt: number
}
type DeliveryTimeType = {
  id: number
  type: 'DURATION'
  name: string
  additionalSla: {
    max_duration: number
    min_duration: number
  }
}
type DelivieriesType = {
  pool_number: number
  order_status: string | null
  order_location_quadrant: number
  order_id: number
  delivery_time: DeliveryTimeType
}
export interface OrderDetailInterface {
  type: 'INSTANT' | 'SUPER' | 'GRAB_MART' | 'ASTRO_BALANCE' | 'KITCHEN' | 'SUPER_V2' | 'MEMBERSHIP'
  invoice: string
  sla: string
  orderCreatedAt: number
  status: string
  note: string
  grabOrderId: string
  grabBookingCode: string
  referenceOrder: string
  reroute_from?: {
    name: string
    id: number
    type: string
  }
  customer: CustomerType
  buyer?: BuyerType
  payment: PaymentType
  timeslot: TimeslotType
  siblings: {
    id: number
    type: 'INSTANT' | 'SUPER' | 'GRAB_MART' | 'ASTRO_BALANCE' | 'KITCHEN' | 'SUPER_V2'
  }[]
  delivery: {
    deliveriesPooled: DelivieriesType[]
    deliveryTime: DeliveryTimeType
  }
}

export type HubType = {
  type: 'HUB' | 'MEGAHUB' | 'MEGAHUB 2.0'
  name: string
}

export type ProductComponentType = {
  rack: string
  name: string
  sku: string
  productQty: number
  scannedQty: number
}
export type ProductModifierType = {
  id: number
  name: string
  modifier_variants: {
    id: string
    name: string
    modifier_type: string
    change_type: string
    price_change: number
  }[]
}

export type TicketDetailType = {
  name: string
  email: string
}

export type ProductType = {
  type: string
  rack: string
  name: string
  sku: string
  price: number
  totalPrice: number
  scannedQty: number
  productQty: number
  components: ProductComponentType[]
  specialType: string
  productModifier: string
  ticketDetail: TicketDetailType
  modifiers: ProductModifierType[]
}

export type StaffType = {
  role: 'PICKER' | 'PACKER' | 'SUPER_PICKER' | 'SUPER_PACKER' | 'DRIVER' | 'SUPER_DRIVER'
  name: string
  phone: string
  externalDriverPartner: string
}

export type ActivityPicType = {
  hub: string
  hubType: 'HUB' | 'MEGAHUB'
  staff: StaffType[]
}

export type OrderProductsInterface = OrderProductsDataType

export type ExternalDriverHistoryLogisticType = {
  provider: string
  service_type: string
  service_code: Nullable<string>
}

export type ExternalDriverHistoryDriverType = {
  full_name: string
  phone_number: string
  plate_number: string
}

export type ExternalDriverHistoryStatusType = {
  last_status: string
  history_status: Array<{
    status: string
    remote_status: string
    remote_status_code: string | number
    remote_description: string
    timestamp: number
  }>
}

export type ExternalDriverHistoryAttributeType = {
  created_at: number
  created_by: string
  updated_at: number
  updated_by: string
  generation: {
    reason: string
    actor: string
  }
  cancelation: {
    reason: string
    actor: string
  }
  use_shipper: boolean
  url_live_track: string
  url_proof_of_delivery: string
}

export type ExternalDriverHistoryDetailsType = {
  id: number
  created_at: number
  created_by: string
  updated_at: number
  updated_by: string
  is_active: boolean
  reference_id: string | number
  remote_reference_id: string
  remoteId: string
  airwaybill_number: string
  logistic: ExternalDriverHistoryLogisticType
  driver: ExternalDriverHistoryDriverType
  status: ExternalDriverHistoryStatusType
}
export interface ExternalDriverHistoryInterface {
  details: ExternalDriverHistoryDetailsType
  attribute: ExternalDriverHistoryAttributeType
}

const defaultExternalDriverHistory: ExternalDriverHistoryInterface[] = []

const defaultOrderProducts: OrderProductsInterface = {
  orderId: 0,
  hub: [],
  items: [],
}

const defaultOrderPic: ActivityPicType[] = []

const defaultOrderDetail: OrderDetailInterface = {
  type: 'INSTANT',
  invoice: '',
  sla: '',
  orderCreatedAt: 0,
  status: '',
  note: '',
  grabOrderId: '',
  grabBookingCode: '',
  referenceOrder: '',
  reroute_from: { name: '', id: 0, type: '' },
  customer: {
    name: '',
    phone: '',
    address: '',
    latitude: 0,
    longitude: 0,
    addressDetail: '',
    driverNote: '',
    labelAddress: '',
    addressImageUrls: [],
  },
  payment: {
    method: '',
    name: '',
    amount: 0,
    partner: '',
    deliveryFee: 0,
    voucher: {
      benefitType: '',
      code: '',
      amount: 0,
      startPeriod: 0,
      endPeriod: 0,
      minPurchase: 0,
    },
    voucherList: null,
    astroBalance: 0,
    totalCoinUsed: 0,
    totalCoinEarned: 0,
    totalPriceItem: 0,
    platformFee: {
      amount: 0,
      paymentFee: 0,
      packagingFee: 0,
      nightFee: 0,
    },
    parentId: 0,
    surgeFee: 0,
  },
  timeslot: {
    minArrivalAt: 1678723200000,
    maxArrivalAt: 1678726800000,
  },
  siblings: [],
  delivery: {
    deliveriesPooled: [],
    deliveryTime: {
      additionalSla: {
        min_duration: 0,
        max_duration: 0,
      },
      id: 0,
      name: '',
      type: 'DURATION',
    },
  },
}

export interface OrderDetailStateInterface {
  orderDetail: OrderDetailInterface
  orderProducts: OrderProductsInterface
  orderEvents: OrderV2EventType[]
  orderPic: ActivityPicType[]
  externalDriverHistory: ExternalDriverHistoryInterface[]
}

const initialState: OrderDetailStateInterface = {
  orderDetail: defaultOrderDetail,
  orderProducts: defaultOrderProducts,
  orderEvents: [],
  orderPic: defaultOrderPic,
  externalDriverHistory: defaultExternalDriverHistory,
}

const orderDetailSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setOrderDetail: (state, action: PayloadAction<OrderDetailInterface>) => {
      state.orderDetail = action.payload
    },
    setOrderProducts: (state, action: PayloadAction<OrderProductsInterface>) => {
      state.orderProducts = action.payload
    },
    setOrderEvents: (state, action: PayloadAction<OrderV2EventType[]>) => {
      state.orderEvents = action.payload
    },
    setOrderPic: (state, action: PayloadAction<ActivityPicType[]>) => {
      state.orderPic = action.payload
    },
    setCustomerAddressImageUrls: (state, action: PayloadAction<string[]>) => {
      state.orderDetail.customer.addressImageUrls = action.payload
    },
    setBuyerInfo: (state, action: PayloadAction<BuyerType | undefined>) => {
      state.orderDetail.buyer = action.payload
    },
    setDriverHistory: (state, action: PayloadAction<ExternalDriverHistoryInterface[]>) => {
      state.externalDriverHistory = action.payload
    },
  },
})

export const {
  setOrderDetail,
  setOrderProducts,
  setOrderEvents,
  setOrderPic,
  setCustomerAddressImageUrls,
  setBuyerInfo,
  setDriverHistory,
} = orderDetailSlice.actions
export default orderDetailSlice.reducer
